import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Users, Crown, Trash2, UserPlus, FolderTree, FileText, Settings, AlertTriangle } from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';
import CategoryManager from './enterprise/CategoryManager';

// #787: helper to format bytes for the Contents tab
function formatBytes(n) {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function Workspaces() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [activeWs, setActiveWs] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState('');
  const [teamPlan, setTeamPlan] = useState('team');
  const [wsTab, setWsTab] = useState('members');
  // #787: new tabs/state for contents + settings
  const [wsContents, setWsContents] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    loadWorkspaces();
  }, []);

  const loadWorkspaces = async () => {
    try {
      const res = await API.get('/api/workspaces');
      setWorkspaces(res.data.workspaces || []);
    } catch {}
    setLoading(false);
  };

  const createWorkspace = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await API.post('/api/workspaces', { name: newName });
      setNewName('');
      setShowCreate(false);
      await loadWorkspaces();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create workspace');
    }
    setCreating(false);
  };

  const loadWorkspaceDetail = async (wsId) => {
    try {
      const res = await API.get(`/api/workspaces/${wsId}`);
      setActiveWs(res.data);
      setRenameValue(res.data?.name || '');
      setConfirmDelete(false);
      setWsContents(null);  // reset — load lazily when Contents tab opens
      setError('');
    } catch {}
  };

  // #787: load the contents summary for the Contents tab
  const loadWorkspaceContents = async () => {
    if (!activeWs) return;
    try {
      const res = await API.get(`/api/workspaces/${activeWs.id}/contents`);
      setWsContents(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load contents');
    }
  };

  // #787: rename via PUT /api/workspaces/{id}
  const renameWorkspace = async () => {
    if (!activeWs || !renameValue.trim()) return;
    setRenaming(true);
    setError('');
    try {
      await API.put(`/api/workspaces/${activeWs.id}`, { name: renameValue.trim() });
      await loadWorkspaces();
      await loadWorkspaceDetail(activeWs.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to rename');
    }
    setRenaming(false);
  };

  // #787: delete via DELETE /api/workspaces/{id}
  const deleteWorkspace = async () => {
    if (!activeWs) return;
    setDeleting(true);
    setError('');
    try {
      await API.delete(`/api/workspaces/${activeWs.id}`);
      setActiveWs(null);
      setConfirmDelete(false);
      await loadWorkspaces();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete');
    }
    setDeleting(false);
  };

  const inviteMember = async () => {
    if (!inviteEmail.trim() || !activeWs) return;
    setInviting(true);
    setError('');
    try {
      await API.post(`/api/workspaces/${activeWs.id}/members`, { email: inviteEmail, role: inviteRole });
      setInviteEmail('');
      await loadWorkspaceDetail(activeWs.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to invite');
    }
    setInviting(false);
  };

  const removeMember = async (memberId) => {
    if (!activeWs) return;
    try {
      await API.delete(`/api/workspaces/${activeWs.id}/members/${memberId}`);
      await loadWorkspaceDetail(activeWs.id);
    } catch {}
  };

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="pt-20 pb-12 px-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold font-['Barlow_Condensed']" data-testid="workspaces-title">Workspaces</h1>
            <p className="text-[#A0A0A0] text-sm mt-1">Collaborate with your team on Frays</p>
          </div>
          <button onClick={() => setShowCreate(true)}
            className="px-4 py-2 bg-[#FF4D00] text-black text-sm font-medium flex items-center gap-2" data-testid="create-workspace-btn">
            <Plus className="w-4 h-4" /> New Workspace
          </button>
        </div>

        {error && <div className="p-3 mb-4 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-sm">{error}</div>}

        {/* Create form */}
        {showCreate && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="p-4 md:p-6 bg-[#1A1A1A] border border-[#2A2A2A] mb-6" data-testid="create-workspace-form">
            <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-4">Create Team Workspace</h3>

            {/* Workspace name */}
            <div className="mb-4">
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-1">WORKSPACE NAME</label>
              <input type="text" value={newName} onChange={e => setNewName(e.target.value)}
                className="w-full px-3 py-2.5 min-h-[44px] bg-[#0F0F0F] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]"
                placeholder="My Team" data-testid="workspace-name-input" />
            </div>

            {/* Plan selection */}
            <div className="mb-4">
              <label className="text-[10px] font-mono text-[#A0A0A0] block mb-2">SELECT PLAN</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={() => setTeamPlan('team')}
                  className={`p-4 border text-left min-h-[44px] transition-colors ${
                    teamPlan === 'team' ? 'border-[#FF4D00] bg-[#FF4D00]/5' : 'border-[#2A2A2A] hover:border-[#555]'
                  }`}
                  data-testid="plan-team"
                >
                  <span className="text-sm font-['Barlow_Condensed'] font-medium block">Team</span>
                  <span className="text-xs text-[#A0A0A0] block mt-0.5">Up to 10 members</span>
                  <span className="text-sm font-bold text-[#FF4D00] mt-2 block">$49/mo</span>
                </button>
                <button
                  onClick={() => setTeamPlan('enterprise')}
                  className={`p-4 border text-left min-h-[44px] transition-colors ${
                    teamPlan === 'enterprise' ? 'border-[#FF4D00] bg-[#FF4D00]/5' : 'border-[#2A2A2A] hover:border-[#555]'
                  }`}
                  data-testid="plan-enterprise"
                >
                  <span className="text-sm font-['Barlow_Condensed'] font-medium block">Enterprise</span>
                  <span className="text-xs text-[#A0A0A0] block mt-0.5">Unlimited members, SSO, audit</span>
                  <span className="text-sm font-bold text-[#FF4D00] mt-2 block">Contact us</span>
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2">
              <button onClick={createWorkspace} disabled={creating || !newName.trim()}
                className="px-6 py-2.5 min-h-[44px] bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2" data-testid="create-ws-submit">
                {creating ? 'Creating...' : 'Create Workspace'}
              </button>
              <button onClick={() => setShowCreate(false)} className="px-4 py-2.5 min-h-[44px] border border-[#2A2A2A] text-sm text-[#A0A0A0]">Cancel</button>
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {workspaces.map((ws, i) => (
            <button
              key={ws.id}
              onClick={() => loadWorkspaceDetail(ws.id)}
              className={`p-4 bg-[#1A1A1A] border text-left transition-all ${activeWs?.id === ws.id ? 'border-[#FF4D00]' : 'border-[#2A2A2A] hover:border-[#FF4D00]/40'}`}
              data-testid={`workspace-card-${i}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#FF4D00]/10 border border-[#FF4D00]/30 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#FF4D00]" />
                </div>
                <div>
                  <span className="font-medium font-['Barlow_Condensed'] block">{ws.name}</span>
                  <span className="text-[10px] text-[#A0A0A0] font-mono capitalize">{ws.role} / {ws.slug}</span>
                </div>
              </div>
            </button>
          ))}
          {!loading && workspaces.length === 0 && !showCreate && (
            <div className="col-span-2 text-center py-12 border border-[#2A2A2A]">
              <Users className="w-8 h-8 text-[#2A2A2A] mx-auto mb-3" />
              <p className="text-[#A0A0A0] text-sm">No workspaces yet. Create one to collaborate.</p>
            </div>
          )}
        </div>

        {/* Workspace Detail */}
        {activeWs && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="border-t border-[#2A2A2A] pt-6">
            <h2 className="text-xl font-semibold font-['Barlow_Condensed'] mb-4">{activeWs.name}</h2>

            {/* Workspace sub-tabs */}
            <div className="flex gap-1 mb-4 flex-wrap">
              <button onClick={() => setWsTab('members')}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-mono min-h-[44px] ${wsTab === 'members' ? 'bg-[#FF4D00] text-black' : 'bg-[#1A1A1A] border border-[#2A2A2A] text-[#A0A0A0]'}`}
                data-testid="ws-tab-members">
                <Users className="w-3.5 h-3.5" /> Members
              </button>
              {/* #787: Contents tab — loads lazily when opened */}
              <button onClick={() => { setWsTab('contents'); if (!wsContents) loadWorkspaceContents(); }}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-mono min-h-[44px] ${wsTab === 'contents' ? 'bg-[#FF4D00] text-black' : 'bg-[#1A1A1A] border border-[#2A2A2A] text-[#A0A0A0]'}`}
                data-testid="ws-tab-contents">
                <FileText className="w-3.5 h-3.5" /> Contents
              </button>
              <button onClick={() => setWsTab('categories')}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-mono min-h-[44px] ${wsTab === 'categories' ? 'bg-[#FF4D00] text-black' : 'bg-[#1A1A1A] border border-[#2A2A2A] text-[#A0A0A0]'}`}
                data-testid="ws-tab-categories">
                <FolderTree className="w-3.5 h-3.5" /> Categories
              </button>
              {/* #787: Settings tab — only shown to owners */}
              {activeWs.owner_id === user?.id && (
                <button onClick={() => setWsTab('settings')}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-mono min-h-[44px] ${wsTab === 'settings' ? 'bg-[#FF4D00] text-black' : 'bg-[#1A1A1A] border border-[#2A2A2A] text-[#A0A0A0]'}`}
                  data-testid="ws-tab-settings">
                  <Settings className="w-3.5 h-3.5" /> Settings
                </button>
              )}
            </div>

            {wsTab === 'members' && (
              <>
                {/* Invite */}
                <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] mb-4">
                  <h3 className="text-xs font-mono text-[#A0A0A0] mb-3">INVITE MEMBER</h3>
                  <div className="flex gap-2">
                    <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                      className="flex-1 px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs focus:outline-none focus:border-[#FF4D00]"
                      placeholder="member@company.com" data-testid="invite-member-email" />
                    <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
                      className="px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs text-[#F5F5F5]" data-testid="invite-role-select">
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                      <option value="viewer">Viewer</option>
                    </select>
                    <button onClick={inviteMember} disabled={inviting || !inviteEmail.trim()}
                      className="px-4 py-2 bg-[#FF4D00] text-black text-xs font-medium disabled:opacity-50 flex items-center gap-1" data-testid="invite-submit">
                      <UserPlus className="w-3.5 h-3.5" /> {inviting ? '...' : 'Invite'}
                    </button>
                  </div>
                </div>

                {/* Members list */}
                <div className="space-y-2">
                  <h3 className="text-xs font-mono text-[#A0A0A0] mb-2">MEMBERS ({activeWs.members?.length || 0})</h3>
                  {activeWs.members?.map((m, i) => (
                    <div key={i} className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#2A2A2A] flex items-center justify-center text-xs font-bold font-['Barlow_Condensed'] text-[#F5F5F5]">
                          {(m.name || m.email || '?').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <span className="text-sm font-medium">{m.name || m.email}</span>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[9px] font-mono text-[#A0A0A0]">{m.email}</span>
                            <span className={`text-[8px] px-1.5 py-0.5 border font-mono capitalize ${
                              m.role === 'owner' ? 'text-[#FF4D00] border-[#FF4D00]/30' : 'text-[#A0A0A0] border-[#2A2A2A]'
                            }`}>
                              {m.role === 'owner' && <Crown className="w-2.5 h-2.5 inline mr-0.5" />}{m.role}
                            </span>
                          </div>
                        </div>
                      </div>
                      {m.role !== 'owner' && m.id !== user?.id && (
                        <button onClick={() => removeMember(m.id)} className="p-1.5 text-[#A0A0A0] hover:text-[#EF4444]">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {wsTab === 'categories' && (
              <div className="bg-[#1A1A1A] border border-[#2A2A2A] p-4">
                <CategoryManager wsId={activeWs.id} scope="team" />
              </div>
            )}

            {/* #787: Contents tab — contexts + counts */}
            {wsTab === 'contents' && (
              <div className="space-y-4" data-testid="ws-contents-panel">
                {!wsContents ? (
                  <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] text-[#A0A0A0] text-sm text-center">
                    Loading contents…
                  </div>
                ) : (
                  <>
                    {/* Summary strip */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
                        <div className="text-[10px] font-mono text-[#A0A0A0]">CONTEXTS</div>
                        <div className="text-lg font-semibold font-['Barlow_Condensed']" data-testid="ws-contexts-count">
                          {wsContents.contexts_count}
                        </div>
                      </div>
                      <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
                        <div className="text-[10px] font-mono text-[#A0A0A0]">MEMBERS</div>
                        <div className="text-lg font-semibold font-['Barlow_Condensed']">
                          {wsContents.members_count}
                        </div>
                      </div>
                      <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
                        <div className="text-[10px] font-mono text-[#A0A0A0]">STORAGE</div>
                        <div className="text-lg font-semibold font-['Barlow_Condensed']">
                          {formatBytes(wsContents.storage_bytes)}
                        </div>
                      </div>
                    </div>

                    {/* Context list */}
                    {wsContents.contexts.length === 0 ? (
                      <div className="p-6 bg-[#1A1A1A] border border-[#2A2A2A] text-center text-sm text-[#A0A0A0]">
                        No contexts in this workspace yet.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <h3 className="text-xs font-mono text-[#A0A0A0]">
                          CONTEXTS ({wsContents.contexts.length}
                          {wsContents.contexts_count > wsContents.contexts.length && ` of ${wsContents.contexts_count}, showing latest 50`})
                        </h3>
                        {wsContents.contexts.map((c) => (
                          <div key={c.id} className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] flex items-start gap-3">
                            <FileText className="w-4 h-4 text-[#A0A0A0] mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-[#F5F1E8] truncate">
                                {c.title || c.filename || '(untitled)'}
                              </div>
                              <div className="flex items-center gap-2 mt-1 text-[10px] font-mono text-[#A0A0A0]">
                                <span className="capitalize">{c.type || 'document'}</span>
                                {c.filename && <span>· {c.filename}</span>}
                                {c.file_size_bytes ? <span>· {formatBytes(c.file_size_bytes)}</span> : null}
                                {c.char_count ? <span>· {c.char_count.toLocaleString()} chars</span> : null}
                                {c.binary_status === 'not_retained' && (
                                  <span className="text-[#F2A900]">· markdown only</span>
                                )}
                                {c.binary_status === 'stored' && (
                                  <span className="text-[#22C55E]">· original available</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* #787: Settings tab — rename + delete (owner-only) */}
            {wsTab === 'settings' && activeWs.owner_id === user?.id && (
              <div className="space-y-4" data-testid="ws-settings-panel">
                {/* Rename */}
                <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                  <h3 className="text-xs font-mono text-[#A0A0A0] mb-3">RENAME WORKSPACE</h3>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      className="flex-1 px-3 py-2 min-h-[44px] bg-[#0F0F0F] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]"
                      placeholder="Workspace name"
                      data-testid="ws-rename-input"
                    />
                    <button
                      onClick={renameWorkspace}
                      disabled={renaming || !renameValue.trim() || renameValue === activeWs.name}
                      className="px-4 py-2 min-h-[44px] bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-50"
                      data-testid="ws-rename-submit"
                    >
                      {renaming ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                  <p className="text-[10px] font-mono text-[#A0A0A0] mt-2">
                    Slug updates automatically from the new name.
                  </p>
                </div>

                {/* Delete — danger zone */}
                <div className="p-4 bg-[#EF4444]/5 border border-[#EF4444]/30">
                  <div className="flex items-start gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4 text-[#EF4444] shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-xs font-mono text-[#EF4444]">DELETE WORKSPACE</h3>
                      <p className="text-[11px] text-[#A0A0A0] mt-1">
                        Removes the workspace, all member associations, categories, and playbook groups.
                        Contexts and fray assets become personal (detached from the workspace) — not deleted.
                      </p>
                    </div>
                  </div>
                  {user?.workspace_id === activeWs.id ? (
                    <p className="text-[11px] text-[#F2A900] italic">
                      This is your active workspace. Switch to another one before deleting it.
                    </p>
                  ) : !confirmDelete ? (
                    <button
                      onClick={() => setConfirmDelete(true)}
                      className="px-4 py-2 min-h-[44px] border border-[#EF4444]/40 text-[#EF4444] text-sm font-medium hover:bg-[#EF4444]/10"
                      data-testid="ws-delete-btn"
                    >
                      Delete Workspace
                    </button>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={deleteWorkspace}
                        disabled={deleting}
                        className="px-4 py-2 min-h-[44px] bg-[#EF4444] text-white text-sm font-medium disabled:opacity-50"
                        data-testid="ws-delete-confirm"
                      >
                        {deleting ? 'Deleting…' : `Yes, delete "${activeWs.name}"`}
                      </button>
                      <button
                        onClick={() => setConfirmDelete(false)}
                        className="px-4 py-2 min-h-[44px] border border-[#2A2A2A] text-sm text-[#A0A0A0]"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
